// src/components/Settings.js
import React, { useState } from 'react';
import Box from '@mui/joy/Box';
import Tabs from '@mui/joy/Tabs';
import Tab from '@mui/joy/Tab';
import TabList from '@mui/joy/TabList';
import TabPanel from '@mui/joy/TabPanel';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Divider from '@mui/joy/Divider';
import InventoryDefaultsPanel from './settings/InventoryDefaultsPanel';
import NotificationPreferencesPanel from './settings/NotificationPreferencesPanel';
import AutomationSettingsPanel from './settings/AutomationSettingsPanel';

// Icons
import InventoryIcon from '@mui/icons-material/Inventory';
import NotificationsIcon from '@mui/icons-material/Notifications';
import TuneIcon from '@mui/icons-material/Tune';
import AutomationIcon from '@mui/icons-material/SmartToy';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

export default function Settings() {
  const [tab, setTab] = useState(0);

  // Theme color for settings page
  const settingsColor = '#FFD700'; // Using the settings color from navItemColors

  return (
    <Box sx={{ 
      minHeight: '100vh',
      pt: 10,
      pb: 4,
      px: { xs: 2, sm: 3, md: 4 },
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)'
    }}>
      {/* Header */}
      <MotionBox
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        sx={{ mb: 4 }}
      >
        <Sheet 
          variant="soft"
        sx={{ 
          p: 3, 
            borderRadius: 'xl',
            background: 'linear-gradient(135deg, #C0C0C0 0%, #E5E5E5 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center'
        }}
      >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <TuneIcon sx={{ fontSize: 32, mr: 2, color: '#333333' }} />
            <Typography 
              level="h1" 
              sx={{ 
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 'bold',
                color: '#333333',
                textAlign: 'center'
              }}
            >
              Settings
            </Typography>
          </Box>
          <Typography 
            level="body-lg" 
            sx={{ 
              color: '#666666',
              textAlign: 'center',
              maxWidth: 600,
              mx: 'auto'
            }}
          >
            Configure inventory defaults, automation, and notification preferences
          </Typography>
        </Sheet>
      </MotionBox>

      {/* Tabs Navigation */}
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        sx={{ mb: 4 }}
      >
        <Tabs
          value={tab}
          onChange={(event, newValue) => setTab(newValue)}
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <TabList
            variant="soft"
            sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
              backdropFilter: 'blur(30px)',
              border: '1px solid rgba(255, 215, 0, 0.2)',
              borderRadius: '20px',
              p: 1.5,
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: { xs: 0.5, sm: 1 },
              width: { xs: '100%', sm: 'auto' },
              maxWidth: { xs: '100%', sm: '800px' },
              mx: 'auto',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              '& .MuiTab-root': {
                py: { xs: 1.5, sm: 4 },
                px: { xs: 1, sm: 4 },
                transition: 'all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.7)',
                borderRadius: '16px',
                minHeight: { xs: 60, sm: 100 },
                fontSize: { xs: '0.75rem', sm: '1rem' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 0.5, sm: 1.5 },
                position: 'relative',
                overflow: 'hidden',
                background: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%)',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  zIndex: 0
                },
                '&.Mui-selected': {
                  color: '#FFD700',
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 215, 0, 0.1) 100%)',
                  boxShadow: '0 8px 25px rgba(255, 215, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 215, 0, 0.4)',
                  transform: 'translateY(-3px) scale(1.02)',
                  '&::before': {
                    opacity: 1
                  }
                },
                '&:hover:not(.Mui-selected)': {
                  background: 'rgba(255, 255, 255, 0.08)',
                  color: '#ffffff',
                  transform: 'translateY(-2px)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)'
                },
                '& .MuiSvgIcon-root': {
                  fontSize: { xs: 20, sm: 32 },
                  position: 'relative',
                  zIndex: 1
                },
                '& .MuiTypography-root': {
                  position: 'relative',
                  zIndex: 1,
                  textAlign: 'center',
                  lineHeight: 1.2
                }
              }
            }}
          >
            <Tab>
              <InventoryIcon />
              <Typography level="title-sm" sx={{ fontWeight: 'inherit' }}>
                Inventory
              </Typography>
              <Typography level="body-xs" sx={{ 
                color: 'rgba(255, 255, 255, 0.5)', 
                display: { xs: 'none', md: 'block' },
                fontSize: '0.75rem'
              }}>
                Units & Locations
              </Typography>
            </Tab>
            <Tab>
              <NotificationsIcon />
              <Typography level="title-sm" sx={{ fontWeight: 'inherit' }}>
                Notifications
              </Typography>
              <Typography level="body-xs" sx={{ 
                color: 'rgba(255, 255, 255, 0.5)', 
                display: { xs: 'none', md: 'block' },
                fontSize: '0.75rem'
              }}>
                Alerts & Emails
              </Typography>
            </Tab>
            <Tab>
              <AutomationIcon />
              <Typography level="title-sm" sx={{ fontWeight: 'inherit' }}>
                Automation
              </Typography>
              <Typography level="body-xs" sx={{ 
                color: 'rgba(255, 255, 255, 0.5)', 
                display: { xs: 'none', md: 'block' },
                fontSize: '0.75rem'
              }}>
                Order Processing
              </Typography>
            </Tab>
          </TabList>
        </Tabs>
      </MotionBox>

      {/* Tab Content Panels */}
      <MotionBox
        key={tab}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Sheet 
          variant="soft"
          sx={{ 
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: '20px',
            minHeight: 500,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
            backdropFilter: 'blur(30px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
          }}
        >
          {tab === 0 && <InventoryDefaultsPanel />}
          {tab === 1 && <NotificationPreferencesPanel />}
          {tab === 2 && <AutomationSettingsPanel />}
        </Sheet>
      </MotionBox>
    </Box>
  );
}
